import { zodResolver } from "@hookform/resolvers/zod"
import { useAtomValue } from "jotai"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import * as z from "zod"
import { authApi } from "@/api/client/auth"
import { useSearchAddressByPostalCode } from "@/api/client/postal-code"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { sessionUserAtom, useSessionRefetch } from "@/store/session"

const completeProfileSchema = z.object({
  lastName: z.string().min(1, "姓を入力してください"),
  firstName: z.string().min(1, "名を入力してください"),
  lastNameKana: z.string().optional(),
  firstNameKana: z.string().optional(),
  phone: z
    .string()
    .min(1, "電話番号を入力してください")
    .regex(/^\d{10,11}$/, "電話番号は10〜11桁の数字で入力してください"),
  emergencyPhone: z
    .string()
    .regex(/^\d{10,11}$/, "緊急用電話番号は10〜11桁の数字で入力してください")
    .optional()
    .or(z.literal("")),
  postalCode: z.string().refine((val) => val === "" || /^\d{7}$/.test(val), {
    message: "郵便番号は7桁の数字で入力してください",
  }),
  address: z.string().min(1, "住所を入力してください"),
})

type CompleteProfileFormValues = z.infer<typeof completeProfileSchema>

export function CompleteProfilePage() {
  const navigate = useNavigate()
  const sessionUser = useAtomValue(sessionUserAtom)
  const refetchSession = useSessionRefetch()

  const { mutateAsync: updateProfile, isPending } = authApi.useUpdateProfile({
    onSuccess: async () => {
      await refetchSession()
      toast.success("プロフィールを登録しました")
      navigate("/")
    },
    onError: () => {
      toast.error("プロフィールの登録に失敗しました")
    },
  })

  const form = useForm<CompleteProfileFormValues>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      lastName: "",
      firstName: "",
      lastNameKana: "",
      firstNameKana: "",
      phone: "",
      emergencyPhone: "",
      postalCode: "",
      address: "",
    },
  })

  // セッションユーザー情報をフォームの初期値として設定
  useEffect(() => {
    if (sessionUser) {
      form.reset({
        lastName: (sessionUser.lastName as string) ?? "",
        firstName: (sessionUser.firstName as string) ?? "",
        lastNameKana: (sessionUser.lastNameKana as string) ?? "",
        firstNameKana: (sessionUser.firstNameKana as string) ?? "",
        phone: (sessionUser.phoneNumber as string) ?? "",
        emergencyPhone: (sessionUser.emergencyPhoneNumber as string) ?? "",
        postalCode: (sessionUser.postalCode as string) ?? "",
        address: (sessionUser.address as string) ?? "",
      })
    }
  }, [sessionUser, form])

  const { mutateAsync: searchAddress, isPending: isSearchingAddress } =
    useSearchAddressByPostalCode({
      onSuccess: (result) => {
        if (result) {
          form.setValue("address", result.fullAddress)
        } else {
          form.setError("postalCode", {
            message: "該当する住所が見つかりませんでした",
          })
        }
      },
      onError: () => {
        form.setError("postalCode", { message: "住所の検索に失敗しました" })
      },
    })

  const handleAddressSearch = async () => {
    const postalCode = form.getValues("postalCode")
    if (!postalCode || postalCode.length !== 7) {
      form.setError("postalCode", {
        message: "郵便番号は7桁の数字で入力してください",
      })
      return
    }

    await searchAddress(postalCode)
  }

  const handleSubmit = async (values: CompleteProfileFormValues) => {
    try {
      const name = `${values.lastName} ${values.firstName}`
      const nameKana =
        values.lastNameKana && values.firstNameKana
          ? `${values.lastNameKana} ${values.firstNameKana}`
          : ""

      await updateProfile({
        name,
        nameKana,
        phone: values.phone,
        emergencyPhone: values.emergencyPhone ?? "",
        postalCode: values.postalCode ?? "",
        address: values.address,
      })
    } catch (error) {
      console.error("Profile update error:", error)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center px-4 pb-8">
        {/* Logo and Description */}
        <div className="w-full max-w-sm mb-6 text-center">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="AGENTCALL エージェントコール" className="w-full h-auto" />
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">
            サービスをご利用いただくために
            <br />
            プロフィール情報の登録をお願いします。
          </p>
        </div>

        {/* Title */}
        <h2 className="text-lg font-bold text-slate-700 mb-6">プロフィール登録</h2>

        {/* Form */}
        <div className="w-full max-w-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
              {/* Name Fields - Side by Side */}
              <div className="flex gap-2.5">
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        氏名（姓）
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="山田" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        氏名（名）
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="太郎" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Name Kana Fields - Side by Side */}
              <div className="flex gap-2.5">
                <FormField
                  control={form.control}
                  name="lastNameKana"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        かな（姓）
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="やまだ" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstNameKana"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-sm font-semibold text-slate-600">
                        かな（名）
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="たろう" className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Phone Number Field */}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">電話番号</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="01234567890"
                        type="tel"
                        autoComplete="tel"
                        maxLength={11}
                        className="h-11"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "")
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergencyPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      緊急用電話番号（任意）
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="09012345678"
                        type="tel"
                        autoComplete="tel"
                        maxLength={11}
                        className="h-11"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "")
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Postal Code Field */}
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">郵便番号</FormLabel>
                    <div className="flex gap-2.5">
                      <FormControl>
                        <Input
                          placeholder="1234567"
                          maxLength={7}
                          className="h-11 flex-1"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "")
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11"
                        onClick={handleAddressSearch}
                        loading={isSearchingAddress}
                      >
                        住所検索
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address Field */}
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">住所</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="東京都渋谷区..."
                        autoComplete="street-address"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <div className="pt-3">
                <Button
                  type="submit"
                  className="w-full h-11 font-bold"
                  disabled={form.formState.isSubmitting}
                  loading={isPending}
                >
                  登録する
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
